import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

export interface DashboardSelectOption {
  value: string;
  label: string;
}

interface DashboardSelectProps {
  label?: string;
  value: string;
  options: DashboardSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
}

export default function DashboardSelect({
  label,
  value,
  options,
  onChange,
  placeholder = "Selecione",
}: DashboardSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      {label && (
        <span className="mb-1.5 block text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681]">
          {label}
        </span>
      )}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 rounded-2xl border border-white/[0.08] bg-[#1c2128] px-4 py-3 text-left transition-colors hover:border-[#6ea8ff]/30 focus:border-[#6ea8ff]/40 focus:outline-none focus:ring-2 focus:ring-[#6ea8ff]/20"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="truncate text-sm font-medium text-[#f0f6fc]">
          {selectedOption?.label ?? placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[#8b949e] transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 z-50 mt-2 max-h-56 overflow-y-auto rounded-2xl border border-white/[0.08] bg-[#1c2128] py-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.5)]"
        >
          {options.map((option) => {
            const isSelected = option.value === value;

            return (
              <li key={option.value} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors ${
                    isSelected
                      ? "bg-[#6ea8ff]/15 text-[#6ea8ff]"
                      : "text-[#f0f6fc] hover:bg-white/5"
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && <Check className="h-4 w-4 shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
